import React from 'react'
import {PageTitle} from '../../../_metronic/layout/core'
import {useQuery} from 'react-query'
import {Helmet} from 'react-helmet'
import {Processing} from '../../../components/Loaders'
import CRUDTable from '../../../components/table/CRUDTable'
import viewProps from '../viewProps'
import {Button} from 'react-bootstrap'
import ModalWithForm from '../../../components/ModalWithForm'
import {deleteGuardian, fetchGuardiansList} from './api'
import GuardianForm from './Form'
import toast, {Toaster} from 'react-hot-toast'
import {GuardianBase} from './type'
import WithModal from '../../../components/WithModal'
import LinkedStudents from './LinkedStudents'
import DeleteLabel from '../../../components/DeleteLabel'
import DeleteConfirmationModal from '../../../components/DeleteWarning'
import EditLabel from '../../../components/EditLabel'
import PortalDropdown from '../../../components/PortalDropdown'
import ActivePill from '../../../components/ActivePill'
import InactivePill from '../../../components/InactivePill'
import {KTIcon} from '../../../_metronic/helpers'
import MoreInfoLabel from '../../../components/MoreInfoLabel'
import ViewAll from '../../../components/ViewAll'
import TopBarProgress from 'react-topbar-progress-indicator'

const guardiansBreadcrumbs = [
  {
    title: 'Guardian',
    path: '/users/guardians',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const Guardian: React.FC = () => {
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: 'guardians',
    queryFn: fetchGuardiansList,
    refetchOnWindowFocus: false,
  })

  if (isLoading) {
    return <Processing />
  }

  const guardiansData = data.data

  const cellModifier = {
    parent_user_status: ({value}: {value: string}) => {
      switch (Number(value)) {
        case 1:
          return <ActivePill />
        case 0:
          return <InactivePill />
        default:
          return ''
      }
    },
    parent_mobile: ({row}: {row: {original: GuardianBase}}) => {
      return `+${row.original.parent_country_code} ${row.original.parent_mobile}`
    },
    more: ({row}: {row: {original: GuardianBase}}) => {
      const orderedFields = [
        ...Object.entries(viewProps.Guardian.primaryFieldsToLabelMap),
        ...Object.entries(viewProps.Guardian.secondaryFieldsToMap),
      ]
      return (
        <WithModal
          renderModalBody={() => (
            <ViewAll data={row.original} cellModifier={cellModifier} fieldOrder={orderedFields} />
          )}
          modalTitle={
            <span>
              Details of{' '}
              <span className="text-primary">
                {row.original.parent_name || row.original.parent_mobile}
              </span>
            </span>
          }
          modalAttrs={{backdrop: true, size: 'lg'}}
        >
          <MoreInfoLabel />
        </WithModal>
      )
    },
    Actions: ({row}: {row: {original: GuardianBase}}) => {
      return (
        <PortalDropdown triggerProps={{className: 'btn btn-secondary px-0 py-1'}}>
          <span className="dropdown-item p-0">
            <WithModal
              renderModalBody={() => <LinkedStudents guardianId={String(row.original.parent_id)} />}
              modalTitle={
                <span>
                  Students linked with{' '}
                  <span className="text-primary text-capitalize">
                    {row.original.parent_name || row.original.parent_mobile}
                  </span>
                </span>
              }
              modalAttrs={{size: 'lg'}}
              triggerClassName="d-block"
            >
              <div className="px-3 py-2 d-flex align-items-center">
                <KTIcon iconName="profile-user" />
                <span className="ms-2">Linked Students</span>
              </div>
            </WithModal>
          </span>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={'Update Guardian'}
              initialValues={row.original}
              onSubmit={refetch}
              Form={GuardianForm}
              modalAttrs={{size: 'lg'}}
              triggerClassName="d-block"
            >
              <EditLabel />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <DeleteConfirmationModal
              triggerClassName="d-block"
              onDelete={async () => {
                const toastId = toast.loading('Deleting Please wait...')
                try {
                  const response = await deleteGuardian(row.original.parent_id)

                  if (response?.status === false && response?.message) {
                    toast.error(response.message, {id: toastId})
                    return null
                  }
                  await refetch()
                  toast.success('Deleted Successfully', {id: toastId})
                  return response
                } catch (error) {
                  toast.error('Something went wrong, please try again later', {id: toastId})
                  throw error
                }
              }}
            >
              <DeleteLabel />
            </DeleteConfirmationModal>
          </span>
        </PortalDropdown>
      )
    },
  }

  return (
    <>
      <Helmet>
        <title>TrackNAB | Guardians</title>
      </Helmet>
      <PageTitle breadcrumbs={guardiansBreadcrumbs}>Guardians</PageTitle>
      <CRUDTable
        data={guardiansData || []}
        {...viewProps.Guardian}
        fieldToLabelMap={viewProps.Guardian.primaryFieldsToLabelMap}
        cellModifier={cellModifier}
        nonSortableColumns={['More Info', 'Actions']}
        headerExtras={
          <ModalWithForm
            modalTitle={'Add Guardian'}
            triggerClassName="ms-auto"
            modalAttrs={{size: 'lg'}}
            onSubmit={refetch}
            Form={GuardianForm}
          >
            <Button size="sm">Add Guardian</Button>
          </ModalWithForm>
        }
      />
      {isFetching && <TopBarProgress />}

      <Toaster />
    </>
  )
}

export default Guardian
